import React, { useEffect } from 'react';
import { Box, Typography, useTheme } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { redirectToMiraOnMyExp } from '@/util/miraMigrationRedirect';

export interface MiraMigrationInterstitialProps {
  redirectUrl: string;
}

/**
 * How long the interstitial waits before auto-redirecting. Long enough for
 * an agent to actually read the two lines of copy once at a normal pace,
 * short enough not to feel like a stall — the manual fallback link is
 * always active immediately, for anyone who doesn't want to wait.
 */
export const AUTO_REDIRECT_DELAY_MS = 10000;

/**
 * Standalone Mira -> My eXp Assistant migration hard redirect: a full-screen
 * takeover that replaces the chat pane entirely (the sidebar/left nav is
 * rendered by `Layout`, a sibling of `ChatPage`, so it stays) and
 * automatically routes the agent to Mira on My eXp after
 * `AUTO_REDIRECT_DELAY_MS`. Always renders a manual fallback link, active
 * immediately, in case the agent doesn't want to wait or the automatic
 * redirect doesn't fire.
 */
export default function MiraMigrationInterstitial({
  redirectUrl,
}: MiraMigrationInterstitialProps): React.ReactElement {
  const { t } = useTranslation('migration');
  const theme = useTheme();

  useEffect(() => {
    const timer = setTimeout(() => {
      redirectToMiraOnMyExp(redirectUrl);
    }, AUTO_REDIRECT_DELAY_MS);
    return () => clearTimeout(timer);
    // Fire once per mount only — re-running on every render would fight
    // back/forward navigation and repeatedly reschedule the redirect.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Box
      data-testid="mira-migration-interstitial"
      sx={{
        height: '100dvh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        textAlign: 'center',
        padding: 3,
        bgcolor: theme.palette.background.default,
      }}
    >
      <Typography
        variant="h4"
        fontWeight="bold"
        gutterBottom
        data-testid="mira-migration-interstitial-title"
      >
        {t('interstitialTitle')}
      </Typography>
      <Typography
        variant="body1"
        color="text.secondary"
        sx={{ mb: 2 }}
        data-testid="mira-migration-interstitial-intro"
      >
        {t('interstitialIntro')}
      </Typography>
      <Typography
        variant="body2"
        color="text.secondary"
        sx={{ mb: 3 }}
        data-testid="mira-migration-interstitial-subtitle"
      >
        {t('interstitialSubtitle')}
      </Typography>
      <Box
        component="button"
        data-testid="mira-migration-interstitial-fallback"
        onClick={() => redirectToMiraOnMyExp(redirectUrl)}
        sx={{
          background: 'none',
          border: 'none',
          textDecoration: 'underline',
          cursor: 'pointer',
          color: theme.palette.primary.main,
          fontSize: '1rem',
          fontWeight: 600,
        }}
      >
        {t('interstitialFallbackLabel')}
      </Box>
    </Box>
  );
}
